import { useContext, createContext } from 'react';
import { jsx } from 'react/jsx-runtime';
import { IntlMessageFormat } from 'intl-messageformat';

const I18nContext = /*#__PURE__*/createContext({
  locale: 'en-US'
});
function I18nProvider(props) {
  const {
    children,
    ...rest
  } = props;
  return /*#__PURE__*/jsx(I18nContext.Provider, {
    value: rest,
    children: props.children
  });
}

/**
 * @deprecated prefer `useI18n()` instead
 */
function useLocale() {
  const ctx = useContext(I18nContext);
  return ctx.locale;
}
function useI18n() {
  return useContext(I18nContext);
}

const defaultLocale = 'en-US';
function getMessageInLocale(messages, id, locale) {
  if (messages[locale] && messages[locale][id]) {
    return messages[locale][id];
  }
  const language = locale.split('-')[0];
  if (messages[language] && messages[language][id]) {
    return messages[language][id];
  }
  for (const key of Object.keys(messages)) {
    if (key.startsWith(language + '-') && messages[key][id]) {
      return messages[key][id];
    }
  }
  return messages[defaultLocale][id];
}

/**
 * Creates a message formatter, which can be used to internationalize text from a message definition.
 * See the formatjs docs for more information on how to define messages that include plurals and more
 * advanced features.
 *
 * @see https://formatjs.io/docs/intl-messageformat/
 */
function createMessageFormatter(messages, locale) {
  const formatMessage = (id, values) => {
    const msg = new IntlMessageFormat(getMessageInLocale(messages, id, locale), locale);
    const res = msg.format(values);
    if (Array.isArray(res)) {
      return res.join('');
    }
    return res;
  };
  const formatReactMessage = (id, values) => {
    const msg = new IntlMessageFormat(getMessageInLocale(messages, id, locale), locale);

    // When getting the list of message chunks from a user provided
    // function (probably a React component), IntlMessageFormat
    // doesn't give the caller an index to key their elements by.
    // We track our own with this.
    const entriesWithIndexProxy = Object.entries(values || {}).map(x => {
      const [key, value] = x;

      // Skip non-function entries.
      if (typeof value !== 'function') {
        return x;
      }

      // This index has a range related to just the
      // count of chunks in the one message (i.e.,
      // this isn't a global that goes up forever)
      let i = 0;
      const next = p => {
        return value(p, ++i);
      };
      return [key, next];
    });
    const indexedValues = Object.fromEntries(entriesWithIndexProxy);
    return msg.format(indexedValues);
  };
  return {
    formatMessage,
    formatReactMessage
  };
}

/**
 * Using the current locale context:
 * Create a message formatter, which can be used to internationalize text from a message definition.
 * See the formatjs docs for more information on how to define messages that include plurals and more
 * advanced features.
 *
 * @see https://formatjs.io/docs/intl-messageformat/
 */
function useMessageFormatter(messages) {
  const {
    locale
  } = useI18n();
  return createMessageFormatter(messages, locale);
}

/**
 * Combine multiple message definitions into a single one for usage in the message formatter.
 */
function combineMessages(message1, message2, message3, message4) {
  // Why not have a params arg? TypeScript can't seem to infer the various types such
  // that they can be intersected for the return type. Four message sources seems like enough.
  const messages = [message1, message2, message3, message4].filter(x => x);
  const target = {};
  for (const msgDef of messages) {
    const locales = Object.keys(msgDef);
    for (const locale of locales) {
      if (!target[locale]) {
        target[locale] = {};
      }
      const entries = msgDef[locale];
      Object.assign(target[locale], entries);
    }
  }
  return target;
}

/**
 * Creates a currency formatter, defaulting to the application's
 * current locale as specified in the root Provider component.
 *
 * @param locale The ISO locale or language code to format in.
 */
function useCurrencyFormatter(locale) {
  const {
    locale: userLocale
  } = useI18n();
  return options => {
    const {
      value,
      currency,
      currencySign = 'standard'
    } = options;
    const pickedLocale = locale || userLocale;
    const numFmt = new Intl.NumberFormat(pickedLocale, {
      style: 'currency',
      currencySign,
      currency
    });
    numFmt.format(value);
    return numFmt.format(value);
  };
}

/**
 * Returns the currency symbol and position (prefix or suffix) for a given
 * currency in the given locale.
 */
function getCurrencyFormat(currency, locale) {
  const string = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(0);
  const symbol = string.replace(/\d/g, '').trim();
  const symbolPosition = string.indexOf(symbol) > 0 ? 'suffix' : 'prefix';
  return {
    symbol,
    symbolPosition
  };
}

/**
 * Parses a string into a number using the thousands and decimal separators for a given locale.
 */
function parseFloatLocale(str, locale) {
  const {
    decimalSymbol,
    separatorSymbol
  } = getNumberFormat(locale);

  // Sanity check on locale symbols; this implementation will not work as expected
  // if one of the symbols is a legal character according to parseFloat, apart from `.`
  const floatAcceptedSymbols = /[-+0-9Ee]/;
  if (floatAcceptedSymbols.test(decimalSymbol) || floatAcceptedSymbols.test(separatorSymbol)) {
    throw new Error(`locale ${locale} not supported by parseFloatLocale`);
  }
  str = str.replace(/./g, match => match === separatorSymbol ? '' : match === decimalSymbol ? '.' : match === '.' ? decimalSymbol : match);
  return parseFloat(str);
}

/**
 * Returns the symbols used for thousands and decimal separators in the given locale.
 */
function getNumberFormat(locale) {
  const groups = new Intl.NumberFormat(locale, {
    style: 'decimal',
    useGrouping: true,
    minimumFractionDigits: 1
  }).formatToParts(10000);
  const separatorGroup = groups.find(group => group.type === 'group');
  const decimalGroup = groups.find(group => group.type === 'decimal');
  if (!separatorGroup || !decimalGroup) {
    throw new Error(`could not determine number format for locale ${locale}`);
  }
  return {
    separatorSymbol: separatorGroup.value,
    decimalSymbol: decimalGroup.value
  };
}

export { I18nProvider, combineMessages, createMessageFormatter, getCurrencyFormat, getNumberFormat, parseFloatLocale, useCurrencyFormatter, useI18n, useLocale, useMessageFormatter };
